import { ApiError, API_ERROR_CODES } from '../api';

/**
 * Type guard to check if an error is an ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Extract a user-friendly error message from any error type.
 * Provides specific handling for ApiError instances.
 */
export function getErrorMessage(error: unknown, defaultMessage = 'An unexpected error occurred'): string {
  if (isApiError(error)) {
    // Handle specific error codes
    if (error.code === API_ERROR_CODES.CLOCK_SKEW) {
      return 'Your session expired due to a server time synchronization issue. Contact your administrator to resolve this.';
    }
    if (error.code === API_ERROR_CODES.TOKEN_EXPIRED) {
      return 'Your session has expired. Log in again to continue.';
    }
    if (error.code === API_ERROR_CODES.UNAUTHORIZED) {
      return 'You are not authorized to perform this action.';
    }
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return defaultMessage;
}

/**
 * Check if an error is a network error (fetch failed).
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  if (error instanceof Error && error.message.includes('NetworkError')) {
    return true;
  }
  return false;
}

/**
 * Check if an error is an authentication error.
 */
export function isAuthError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 401 || error.status === 403;
  }
  return false;
}

/**
 * Get HTTP status code from error if available.
 */
export function getErrorStatusCode(error: unknown): number | undefined {
  if (isApiError(error)) {
    return error.status;
  }
  return undefined;
}
